import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { syncAppBadgeToUnread } from '@/utils/appBadge';
import { logger } from '@/utils/logger';

export type AppNotification = {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: string;
  data: Record<string, unknown>;
  read: boolean;
  created_at: string;
  /**
   * When the row last changed. A chat notification is one row per thread, rewritten as
   * new messages arrive, so it is updated_at -- not created_at -- that says how recent
   * the conversation is. Backfilled to created_at for every pre-existing row by
   * 20260909120000_notifications_collapsible_push.sql.
   */
  updated_at: string;
};

function errorMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  if (e instanceof Error) return e.message;
  return 'Failed to load notifications';
}

/**
 * @param authLoading — while true, keeps loading state and does not fetch (avoids empty flash before session uid is known).
 */
/** Rows per request. The query was previously unbounded, so a user with a long
 *  history downloaded every notification they had ever received on each mount. */
export const NOTIFICATIONS_PAGE_SIZE = 50;

export function useNotifications(userId: string | undefined, authLoading = false) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: qErr } = await supabase
        .from('notifications')
        .select('id, user_id, title, message, type, data, read, created_at, updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .range(0, NOTIFICATIONS_PAGE_SIZE - 1);

      if (qErr) throw qErr;
      const rows = (data ?? []) as AppNotification[];
      setNotifications(rows);
      setHasMore(rows.length === NOTIFICATIONS_PAGE_SIZE);
    } catch (e) {
      logger.error('useNotifications', e);
      setError(errorMessage(e));
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [userId, authLoading]);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!userId) return { ok: false as const };
      try {
        const { error: uErr } = await supabase
          .from('notifications')
          .update({ read: true })
          .eq('id', id)
          .eq('user_id', userId);
        if (uErr) throw uErr;
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
        // Reading a row changes the unread count -- keep the icon badge in step
        void syncAppBadgeToUnread(userId);
        return { ok: true as const };
      } catch (e) {
        logger.error('useNotifications markAsRead', e);
        return { ok: false as const };
      }
    },
    [userId],
  );

  /**
   * Mark every unread notification as read -- including rows on pages not loaded yet.
   * Feeds the push badge count (the server sends badge = unread rows), so this is
   * what "clears" the app-icon badge for future pushes.
   *
   * Only ever an explicit "Mark all as read", so the list on screen follows at once.
   */
  const markAllAsRead = useCallback(async () => {
    if (!userId) return { ok: false as const };
    try {
      const { error: uErr } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false);
      if (uErr) throw uErr;
      setNotifications((prev) => prev.map((n) => (n.read ? n : { ...n, read: true })));
      void syncAppBadgeToUnread(userId);
      return { ok: true as const };
    } catch (e) {
      logger.error('useNotifications markAllAsRead', e);
      return { ok: false as const };
    }
  }, [userId]);

  const deleteNotification = useCallback(
    async (id: string) => {
      if (!userId) return { ok: false as const };
      try {
        const { error: dErr } = await supabase
          .from('notifications')
          .delete()
          .eq('id', id)
          .eq('user_id', userId);
        if (dErr) throw dErr;
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        // Deleting an unread row changes the unread count — keep the icon badge in step
        void syncAppBadgeToUnread(userId);
        return { ok: true as const };
      } catch (e) {
        logger.error('useNotifications deleteNotification', e);
        return { ok: false as const };
      }
    },
    [userId],
  );

  const deleteAllNotifications = useCallback(async () => {
    if (!userId) return { ok: false as const };
    try {
      const { error: dErr } = await supabase.from('notifications').delete().eq('user_id', userId);
      if (dErr) throw dErr;
      setNotifications([]);
      void syncAppBadgeToUnread(userId);
      return { ok: true as const };
    } catch (e) {
      logger.error('useNotifications deleteAllNotifications', e);
      return { ok: false as const };
    }
  }, [userId]);

  /** Append the next page. No-op while a load is in flight or nothing is left. */
  const loadMore = useCallback(async () => {
    if (!userId || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const from = notifications.length;
      const { data, error: qErr } = await supabase
        .from('notifications')
        .select('id, user_id, title, message, type, data, read, created_at, updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .range(from, from + NOTIFICATIONS_PAGE_SIZE - 1);
      if (qErr) throw qErr;
      const rows = (data ?? []) as AppNotification[];
      setNotifications((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...rows.filter((n) => !seen.has(n.id))];
      });
      setHasMore(rows.length === NOTIFICATIONS_PAGE_SIZE);
    } catch (e) {
      logger.error('useNotifications loadMore', e);
    } finally {
      setLoadingMore(false);
    }
  }, [userId, notifications.length, loadingMore, hasMore]);

  return {
    notifications,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    error,
    refetch: fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
  };
}
